import config from '../config/config';
import { I18N } from 'aurelia-i18n';

export class Portal {
	theme: any;
	settings: any;
	ezpay: any;
	fields: any;
	businessSettings: any;
	paymentMethods: any;
	paymentTypes: any;
	hasAccountTypeFor3rdParty: any;
	portalRegistrationWithoutZipCode: any;
	requireMinPayment: any;
	feeSettings: any;
	sendPlanReceiptToMerchant: any;
	merchantConfiguredReceiptEmails: any;
	cardsAccepted: any;
	accountNumberSettings: any;
	accountNumberLabel: string;
	accountNumberTooltip: any;
	minimumPaymentAmount: any;
	maximumPaymentAmount: any;
	showMinMaxPaymentVerbiage: any;
	displayMinimumPaymentAmountOnPortal: boolean;
	id: any;
	dateUpdated: any;
	active: boolean;

	constructor(portal: any, private i18n:I18N) {
		this.setPortalDefaults(portal);
	}

	setPortalDefaults(portal: any) {
		// String settings first, then override with any changes that have been made while the site is running. This is mostly a problem
		//// when the user refreshes; otherwise, we never re-hydrate the Portal.
		let settings = JSON.parse(portal.settings);
		Object.assign(this, settings);
		this.theme = JSON.parse(portal.settings).theme;
		Object.assign(this, portal);
		this.settings = {};
		Object.assign(this.settings, settings);
		this.requireProperty(this, 'ezpay');
		this.requireProperty(this.ezpay, 'paymentTypes');
		this.requireProperty(this, 'paymentTypes');
		this.requireProperty(this, 'paymentMethods');
		this.requireProperty(this, 'fields');
		this.requireProperty(this.fields, 'billingAddress');
		this.requireProperty(this.fields, 'billingPhone');
		this.requireProperty(this.fields, 'billingEmail');
		this.requireProperty(this.fields, 'totalAmount');
		this.requireProperty(this.fields, 'transactionDescription');
		this.requireProperty(this.fields, 'cvv');

		if (!this.fields.hasOwnProperty('cardCustom')) {
			this.fields.cardCustom = [
				{},
				{},
				{},
				{},
				{}
			];
		}
		if (!this.fields.hasOwnProperty('checkCustom')) {
			this.fields.checkCustom = [
				{},
				{},
				{},
				{},
				{}
			];
		}

		if (this.ezpay && !this.ezpay.accountNumberHandling) {
			if (this.ezpay.lookupCustomerAccount) {
				// ezpay validation is on. "lookup" account before accepting a payment.
				this.ezpay.accountNumberHandling = 'lookup';
			} else {
				// ezpay validation is off. "allow" payments without validation
				this.ezpay.accountNumberHandling = 'allow';
			}
		}
		if (this.ezpay && !this.ezpay.accountNumberSettings) {
			var ezpaySettings = this.ezpay;
			this.ezpay.accountNumberSettings = [];
			this.accountNumberSettings.forEach(function (setting) {
				ezpaySettings.accountNumberSettings.push(setting);
			});
			this.ezpay = ezpaySettings;
			// 8.2 and previous settings coming in.
			if (!this.ezpay.registrationIdSettings) {
				// If the 8.2 settings were using referenceId as validation attribute, make sure it is enabled in new settings.
				if (this.ezpay.validationAttribute === 'referenceId') {
					this.ezpay.accountNumberSettings[0].required = true;
				}
				// We were using registration id, so we don't want reference ID to be required.
				else {
					this.ezpay.accountNumberSettings[0].required = false;
				}
			}
		}

		if (!this.accountNumberLabel) {
			this.accountNumberLabel = this.i18n.i18next.t('accountNumber', 'Account Number');
		}

		if (!this.theme) {
			this.theme = {
				primaryButtonColor: '#005fb2',
				secondaryButtonColor: '#01892c',
				primaryButtonFontColor: '#FFFFFF',
				secondaryButtonFontColor: '#FFFFFF',
				fontPrimaryColor: '#005fb2',
				fontSecondaryColor: '#01892c'
			}
		}

		this.active = true;
	}

	//Overrides default portal settings.
	applyPortfolioSettings(portfolio: any, isForEzpay: any, merchantKey: number = 0) {
		if (portfolio) {
			let merchantAllowsSinglePayments = isForEzpay ? true : this.businessSettings.allowSinglePayment;
			let currentMerchantSettings = portfolio.merchantSettings;
			if (isForEzpay && merchantKey > 0 && !!portfolio.ezPayMerchants) {
				currentMerchantSettings = {};
				const currentMerchantSelected = portfolio.ezPayMerchants.find(m => m.merchantKey == merchantKey);
				if (!!currentMerchantSelected && currentMerchantSelected.merchantSettings){
					currentMerchantSettings = currentMerchantSelected.merchantSettings;
				}
			}
			if (!currentMerchantSettings || Object.keys(currentMerchantSettings).length == 0) {
				this.applyDefaultPortfolioSettings();
				return;
			}

			// using portfolio. instead of portfolio.settings. because these could have been overridden by merchant settings so we need the "computed" version of these properties.
			this.paymentMethods.allowCreditCard = portfolio.paymentMethods.allowCreditCard;
			this.paymentMethods.allowAch = portfolio.paymentMethods.allowAch;
			this.paymentMethods.allowApplePay = portfolio.paymentMethods.allowApplePay;

			let canAcceptPayments = this.paymentMethods.allowCreditCard || this.paymentMethods.allowAch;
			this.paymentTypes.allowSingle = merchantAllowsSinglePayments && portfolio.settings.paymentTypes.allowSingle && canAcceptPayments;
			this.paymentTypes.allowFuture = merchantAllowsSinglePayments && portfolio.settings.paymentTypes.allowFuture && canAcceptPayments;
			this.paymentTypes.allowNegotiator = this.businessSettings.allowNegotiator && portfolio.settings.paymentTypes.allowNegotiator && canAcceptPayments;
			this.paymentTypes.allowInstallments = this.businessSettings.allowInstallments && portfolio.settings.paymentTypes.allowInstallments && canAcceptPayments;
			this.paymentTypes.allowRehab = this.businessSettings.allowRehab && portfolio.settings.paymentTypes.allowRehab;

			this.setMaxFuturePaymentDays(portfolio, isForEzpay, currentMerchantSettings);

			this.paymentTypes.rehab = {
				payInFull: this.businessSettings.allowRehab && portfolio.settings.paymentTypes.allowRehab && portfolio.settings.paymentTypes.rehab.payInFull && canAcceptPayments
			};

			this.setPaymentAmounts(portfolio, currentMerchantSettings);

			this.hasAccountTypeFor3rdParty = currentMerchantSettings.hasAccountTypeFor3rdParty;
			this.portalRegistrationWithoutZipCode = portfolio.settings.portalRegistrationWithoutZipCode;
			this.requireMinPayment = portfolio.settings.requireMinPayment;
			this.feeSettings = currentMerchantSettings.feeSettings;
			this.sendPlanReceiptToMerchant = currentMerchantSettings.sendPlanReceiptToMerchant;
			this.merchantConfiguredReceiptEmails = currentMerchantSettings.merchantConfiguredReceiptEmails;
			this.cardsAccepted = currentMerchantSettings.cardsAccepted;

			// 6.3.1 - CDW - accountNumberSettings array introduced. Extra code here to port old settings, which had just
			// accountNumberLabel and accountNumberTooltip, into the accountNumberSettings array if needed. Also code here
			// to handle (probably testing only) situation where the format has changed since the portal was defined.
			// Moved here from Business (Merchant) for MMIDs
			// Updated by Ming on 03142018: Add required and visible in accountNumberSettings. In short, this.accountNumberSettings will not be changed for new data.

			if (currentMerchantSettings.accountNumberFormat) {
				// If we have already mapped an accountNumberFormat object into accountNumberSettings
				if (this.accountNumberSettings) {
					// The merchantSettings version of AccountNumberFormat doesn't match the length of the "existing" mapped accountNumberSettings
					if (currentMerchantSettings.accountNumberFormat.segments.length != this.accountNumberSettings.length) {
						// Going from a two-segment accountNumber (CU) to single segment accountNumber
						if (currentMerchantSettings.accountNumberFormat.numberOfSegments === 1 && this.accountNumberSettings.length === 2) {
							this.accountNumberSettings = this.accountNumberSettings.slice(0, 1);

							// Going from a single-segment accountNumber to two-segment accountNumber
						} else if (currentMerchantSettings.accountNumberFormat.numberOfSegments === 2 && this.accountNumberSettings.length === 1) {
							this.accountNumberSettings.push({
								item: currentMerchantSettings.accountNumberFormat.segments[1].label,
								tooltip: '',
								required: currentMerchantSettings.accountNumberFormat.segments[1].required,
								visible: currentMerchantSettings.accountNumberFormat.segments[1].visible
							});
						}
					}

					// We haven't mapped accountNumberFormat into accountNumberSettings yet.
				} else {
					this.accountNumberSettings = currentMerchantSettings.accountNumberFormat.segments.map(item => {
						return {
							label: item.label,
							tooltip: '',
							required: item.required,
							visible: item.visible
						}
					});
				}
				// If we don't have an accountNumberFormat in merchant settings:
			} else {
				if (this.accountNumberSettings) {
					this.accountNumberSettings = this.accountNumberSettings.slice(0, 1);
				} else {
					this.accountNumberSettings = [
						{
							label: this.accountNumberLabel,
							tooltip: this.accountNumberTooltip
						}
					];
				}
			}
		} else {
			this.applyDefaultPortfolioSettings();
		}
	}

	applyDefaultPortfolioSettings() {
		this.paymentTypes.allowSingle = false;
		this.paymentTypes.allowFuture = false;
		this.paymentTypes.allowNegotiator = false;
		this.paymentTypes.allowInstallments = false;
		this.paymentTypes.allowRehab = false;
		this.paymentTypes.rehab = {
			payInFull: false
		};
		this.paymentTypes.maxFutureDays = 1;
		this.paymentMethods.allowCreditCard = false;
		this.paymentMethods.allowAch = false;
		this.accountNumberSettings = {};
		this.hasAccountTypeFor3rdParty = false;
		this.portalRegistrationWithoutZipCode = false;
		this.requireMinPayment = false;
		this.feeSettings = {};
		this.sendPlanReceiptToMerchant = false;
		this.merchantConfiguredReceiptEmails = false;
		this.cardsAccepted = {};
		this.accountNumberSettings = [{}];
	}

	setPaymentAmounts(portfolio: any, currentMerchantSettings: any) {

		//FYI : this.settings = portal settings
		if (parseFloat(this.settings.minimumPaymentAmount) > 0 && parseFloat(portfolio.settings.minimumPaymentAmount) > 0) {
			this.minimumPaymentAmount = Math.max(this.settings.minimumPaymentAmount, portfolio.settings.minimumPaymentAmount);
		}
		else {
			this.minimumPaymentAmount = this.settings.minimumPaymentAmount ? this.settings.minimumPaymentAmount : portfolio.settings.minimumPaymentAmount;
		}

		if (parseFloat(this.settings.maximumPaymentAmount) > 0 && parseFloat(portfolio.settings.maximumPaymentAmount) > 0) {
			this.maximumPaymentAmount = Math.min(this.settings.maximumPaymentAmount, portfolio.settings.maximumPaymentAmount);
		}
		else {
			this.maximumPaymentAmount = this.settings.maximumPaymentAmount ? this.settings.maximumPaymentAmount : portfolio.settings.maximumPaymentAmount;
		}

		if (parseFloat(currentMerchantSettings.maximumPayment) > 0) {
			this.maximumPaymentAmount = currentMerchantSettings.maximumPayment;
		}

		this.showMinMaxPaymentVerbiage = portfolio.settings.showMinMaxPaymentVerbiage;
	}

	setMaxFuturePaymentDays(portfolio: any, isForEzpay: any, currentMerchantSettings: any) {
		// this.PaymentTypes.maxFutureDays is merchant's max future days
		// portfolio.settings.paymentTypes.maxFutureDays is the portfolio's
		// this.settings.ezpay.maxFutureDays is the portal's and should only apply if this is ezpay
		let merchantMaxFutureDays = parseInt(currentMerchantSettings.maxFutureDays);
		let portfolioMaxFutureDays = parseInt(portfolio.settings.paymentTypes.maxFutureDays);
		let portalMaxFutureDays = parseInt(this.settings.ezpay.maxFutureDays);

		// check if merchant, portfolio and portal allow post date payments
		if (!merchantMaxFutureDays) merchantMaxFutureDays = Number.MAX_VALUE;
		if (!portfolioMaxFutureDays || !portfolio.settings.paymentTypes.allowFuture) portfolioMaxFutureDays = Number.MAX_VALUE;
		if (!portalMaxFutureDays || !this.settings.ezpay.paymentTypes.allowFuture) portalMaxFutureDays = Number.MAX_VALUE;

		if (isForEzpay) {
			if (merchantMaxFutureDays > 0 && portfolioMaxFutureDays > 0 && portalMaxFutureDays > 0) {
				if (this.settings.ezpay.accountNumberHandling == 'lookup') {
					//the code logic in this if affects/drives function "applyPortfolioSettingsToPage" values in ezpay.js
					this.paymentTypes.maxFutureDays = Math.min(merchantMaxFutureDays, portfolioMaxFutureDays);
				}
				else {
					this.paymentTypes.maxFutureDays = Math.min(merchantMaxFutureDays, portalMaxFutureDays);
				}
			}
			else {
				this.paymentTypes.maxFutureDays = merchantMaxFutureDays;
			}
		}
		else {
			if (merchantMaxFutureDays > 0 && portfolioMaxFutureDays > 0) {
				this.paymentTypes.maxFutureDays = Math.min(merchantMaxFutureDays, portfolioMaxFutureDays);
			}
			else {
				this.paymentTypes.maxFutureDays = merchantMaxFutureDays;
			}
		}
	}

	applyBusinessSettings(businessSettings) {
		this.businessSettings = businessSettings;

		if (!this.ezpay.paymentMethods) {
			this.ezpay.paymentMethods = {
				allowCreditCard: false,
				allowAch: false,
				allowApplePay: false
			};
		}
		this.ezpay.enabled = this.ezpay.enabled && this.businessSettings.allowEzpay;


		// TODO: MMIDs: Where do custom field labels come from in this new world? Probably need to pull them with the portfolio like nearly everything else.
		// for (let ii = 0 ; ii < 5 ; ++ii) {
		//     if (businessSettings.customFieldLabels[ii]) {
		//         if (!this.fields.cardCustom[ii].label) {
		//             this.fields.cardCustom[ii].label = businessSettings.customFieldLabels[ii];
		//         }
		//         if (!this.fields.checkCustom[ii].label) {
		//             this.fields.checkCustom[ii].label = businessSettings.customFieldLabels[ii];
		//         }
		//     }
		// }
	}

	stringify() {
		return JSON.stringify(this, (key, value) => {
			if (key.toLowerCase() === 'id') { return undefined; }
			if (key.toLowerCase() === 'name') { return undefined; }
			if (key.toLowerCase() === 'settings') { return undefined; }
			if (key.toLowerCase() === 'datecreated') { return undefined; }
			if (key.toLowerCase() === 'dateupdated') { return undefined; }
			if (key.toLowerCase() === 'isactive') { return undefined; }
			if (key.toLowerCase() === 'disclosurenames') { return undefined; }
			if (key.toLowerCase() === 'templatenames') { return undefined; }

			return value;
		});
	}

	requireProperty(object, fieldName) {
		if (!object.hasOwnProperty(fieldName)) {
			object[fieldName] = {};
		}
	}

	fieldSettings(fieldName) {
		if (!this.fields || !this.fields.hasOwnField(fieldName)) { return {}; }
		return this.fields[fieldName];
	}

	//TODO: Chris - add computedFrom
	get url() {
		return `${config.portalUrl}/portal/${this.id}`;
	}

	//TODO: Chris - add computedFrom
	get bannerImageUrl() {
		let updated = new Date(this.dateUpdated).getTime();
		return `${config.serviceUrl}/portal/${this.id}/banner/true?${updated}`;
	}

	//TODO: Chris - add computedFrom
	get logoImageUrl() {
		let updated = new Date(this.dateUpdated).getTime();
		return `${config.serviceUrl}/portal/${this.id}/logo/true?${updated}`;
	}

	hexToRgb(hex) {
		let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result ? {
			r: parseInt(result[1], 16),
			g: parseInt(result[2], 16),
			b: parseInt(result[3], 16)
		} : null;
	}

	//TODO: Chris - add computedFrom
	get styles() {
		let secondaryColorRgb = this.hexToRgb(this.theme.secondaryButtonColor);
		return `
                <style id='portalStyles'>
                    .bt-primary-button {
                      background-color:${this.theme.primaryButtonColor} !important;
                      color:${this.theme.primaryButtonFontColor} !important;
                    }
                    .bt-secondary-button {
                      background-color:${this.theme.secondaryButtonColor} !important;
                      color:${this.theme.secondaryButtonFontColor} !important;
                    }
                    .bt-secondary-box {
                      background-color: rgba(${secondaryColorRgb.r}, ${secondaryColorRgb.g}, ${secondaryColorRgb.b}, 0.2) !important;
                      color:#000 !important;
                    }
                    .bt-primary-header {
                      color:${this.theme.fontPrimaryColor} !important;
                    }
                    .bt-secondary-header {
                      color:${this.theme.fontSecondaryColor} !important;
                    }
                    .bt-primary-line {
                      border-color: ${this.theme.fontPrimaryColor} !important;
                    }
                    .bt-secondary-line {
                      border-bottom: 2px solid ${this.theme.fontSecondaryColor} !important;
                      color: ${this.theme.fontSecondaryColor} !important;
                    }
                    .bt-secondary-line-foot {
	                  border-top: 2px solid ${this.theme.fontSecondaryColor} !important;
                    }
                    .bt-footer {
	                  color: ${this.theme.footerColor} !important;
                    font-size: ${this.theme.footerSize}px !important;
                    }
                    @media (max-width: 480px) {
                      .bt-footer {
                        color: initial !important;
                        font-size: initial !important;
                      }
                    }
                    /*.nav.navbar-nav li.active a:after {
                      color:${this.theme.fontSecondaryColor} !important;
                    }*/
					.bulletin {
                        background-color: ${this.theme.secondaryButtonColor} !important;
                        color: ${this.theme.secondaryButtonFontColor} !important;
                        padding: 15px;
						margin-bottom: 15px;
                    }
                </style>`;
	}
}
